import FormSpecification from './FormSpecification';
import Question from './Question';
import QuestionSlot from './QuestionSlot';
import SlotTypes from './SlotTypes';

export type QuestionLocation = {
  question: Question;
  sectionIndex: number;
  subsectionIndex: number;
  questionIndex: number;
  sectionId: string;
  subsectionId: string;
};

/** Question dont le score dépend de la moyenne PEXBD — dérivée de `scoreCalculatedLater` dans la spec. */
export type ScoreCalculatedLaterEntry = {
  questionId: string;
  title: string;
  number: string;
  fieldPath: string;
  slotIndex: number;
  average: number | string;
};

type ScoreCalculatedLaterSpecEntry = Omit<ScoreCalculatedLaterEntry, 'average'>;

let registryInstance: FormSpecRegistry | null = null;

/**
 * Indexe une spec de formulaire pour retrouver questions et chemins Formik par identifiant (`id`).
 * Les champs `oldId` sur questions/sections/sous-sections sont réservés à la migration Firestore
 * et ne sont pas indexés ni utilisés ici.
 */
class FormSpecRegistry {
  private readonly spec: FormSpecification;

  private readonly questionsById = new Map<string, QuestionLocation>();

  private readonly scoreCalculatedLaterEntries: ScoreCalculatedLaterSpecEntry[] = [];

  constructor(spec: FormSpecification) {
    this.spec = spec;

    spec.sections.forEach((section, sectionIndex) => {
      section.subsections.forEach((subsection, subsectionIndex) => {
        subsection.questions.forEach((question, questionIndex) => {
          const location: QuestionLocation = {
            question,
            sectionIndex,
            subsectionIndex,
            questionIndex,
            sectionId: section.id,
            subsectionId: subsection.id,
          };

          this.questionsById.set(question.id, location);

          question.slots.forEach((slot: QuestionSlot, slotIndex) => {
            if (slot.scoreCalculatedLater && slot.type === SlotTypes.REPEATER) {
              this.scoreCalculatedLaterEntries.push({
                questionId: question.id,
                title: question.title,
                number: question.number,
                fieldPath: `sections[${sectionIndex}].subsections[${subsectionIndex}].questions[${questionIndex}].slots[${slotIndex}]`,
                slotIndex,
              });
            }
          });
        });
      });
    });
  }

  getSpec(): FormSpecification {
    return this.spec;
  }

  getQuestion(questionId: string): Question | undefined {
    return this.questionsById.get(questionId)?.question;
  }

  getLocation(questionId: string): QuestionLocation | undefined {
    return this.questionsById.get(questionId);
  }

  /** Repeaters `scoreCalculatedLater` — score comparé à la moyenne PEXBD à la génération du rapport. */
  getScoreCalculatedLaterEntries(): ScoreCalculatedLaterEntry[] {
    return this.scoreCalculatedLaterEntries.map((entry) => ({
      ...entry,
      average: 0,
    }));
  }

  getQuestionPath(questionId: string): string {
    const location = this.getLocationOrThrow(questionId);
    return `sections[${location.sectionIndex}].subsections[${location.subsectionIndex}].questions[${location.questionIndex}]`;
  }

  getSubsectionQuestionsPath(questionId: string): string {
    const location = this.getLocationOrThrow(questionId);
    return `sections[${location.sectionIndex}].subsections[${location.subsectionIndex}].questions`;
  }

  getSlotPath(questionId: string, slotIndex = 0): string {
    return `${this.getQuestionPath(questionId)}.slots[${slotIndex}]`;
  }

  /** Chemin vers le tableau `data` d'un slot (sans index de donnée). */
  getSlotDataFieldPath(questionId: string, slotIndex = 0): string {
    return `${this.getSlotPath(questionId, slotIndex)}.data`;
  }

  /** Chemin vers une valeur précise dans `data`. */
  getSlotDataPath(questionId: string, slotIndex = 0, dataIndex = 0): string {
    return `${this.getSlotDataFieldPath(questionId, slotIndex)}[${dataIndex}]`;
  }

  /**
   * Chemin relatif à `sectionPath` (ex. `sections[1].subsections[0].questions`).
   * Utilisé par les fonctions `autofill` des questions d'une même sous-section.
   */
  getRelativeSlotDataPath(questionId: string, slotIndex = 0, dataIndex = 0): string {
    const location = this.getLocationOrThrow(questionId);
    return `[${location.questionIndex}].slots[${slotIndex}].data[${dataIndex}]`;
  }

  resolveRelativeSlotData(
    sectionPath: string,
    questionId: string,
    slotIndex = 0,
    dataIndex = 0,
  ): string {
    return `${sectionPath}${this.getRelativeSlotDataPath(questionId, slotIndex, dataIndex)}`;
  }

  private getLocationOrThrow(questionId: string): QuestionLocation {
    const location = this.questionsById.get(questionId);
    if (!location) {
      throw new Error(`Question introuvable dans la spec : ${questionId}`);
    }
    return location;
  }
}

export const initFormSpecRegistry = (spec: FormSpecification): FormSpecRegistry => {
  registryInstance = new FormSpecRegistry(spec);
  return registryInstance;
};

export const getFormSpecRegistry = (): FormSpecRegistry => {
  if (!registryInstance) {
    throw new Error('FormSpecRegistry non initialisé. Appeler initFormSpecRegistry() depuis la spec du formulaire.');
  }
  return registryInstance;
};

export const questionSlotData = (
  sectionPath: string,
  questionId: string,
  slotIndex = 0,
  dataIndex = 0,
): string => getFormSpecRegistry().resolveRelativeSlotData(sectionPath, questionId, slotIndex, dataIndex);

export default FormSpecRegistry;
